/**
 * useAutomationRules — Main orchestration hook for the automation rules engine.
 *
 * PM components (Kanban, Gantt, Calendar) call this hook to enable rule evaluation.
 * Rules only execute in viewer/interactive mode (not editor mode).
 *
 * Flow: handleDataEvent → filter matching rules → evaluate conditions → execute actions
 */

import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import type {
  AutomationRule,
  AutomationDataEvent,
  AutomationRuleExecutionResult,
  WritebackConfig,
  FieldMapping,
} from '@/types/pm-types';
import { evaluateConditions } from '@/utils/automationConditionEvaluator';
import { executeAction, type ActionExecutorContext } from '@/utils/automationActionExecutor';
import { AutomationCycleDetector } from '@/utils/automationCycleDetector';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import { useToast } from '@/hooks/use-toast';

export interface UseAutomationRulesOptions {
  rules: AutomationRule[] | undefined;
  writebackConfig: WritebackConfig | undefined;
  componentId: string;
  fieldMapping?: FieldMapping;
  currentUser?: string;
  enabled?: boolean; // false in editor mode
}

export interface UseAutomationRulesResult {
  handleDataEvent: (event: AutomationDataEvent) => Promise<AutomationRuleExecutionResult[]>;
  detectNewRecords: (records: Record<string, unknown>[]) => void;
  executionLog: AutomationRuleExecutionResult[];
  isProcessing: boolean;
  warnings: string[];
}

export function useAutomationRules(
  options: UseAutomationRulesOptions,
): UseAutomationRulesResult {
  const { rules, writebackConfig, componentId, fieldMapping, currentUser, enabled = false } = options;

  const [executionLog, setExecutionLog] = useState<AutomationRuleExecutionResult[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const isProcessingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Track previous record IDs for onItemAdd detection
  const previousRecordIdsRef = useRef<Set<string>>(new Set());

  const { updateRecord } = useDataWriteback({
    writebackConfig,
    componentId,
  });

  const { toast } = useToast();

  const writebackEnabled = writebackConfig?.enabled ?? false;
  const idField = writebackConfig?.idField ?? '_id';

  // Memoize enabled rules for stable reference
  const enabledRules = useMemo(
    () => (rules ?? []).filter((r) => r.enabled),
    [rules],
  );

  const handleDataEvent = useCallback(
    async (event: AutomationDataEvent): Promise<AutomationRuleExecutionResult[]> => {
      if (!enabled || enabledRules.length === 0) {
        return [];
      }

      // Prevent concurrent processing
      if (isProcessingRef.current) return [];
      isProcessingRef.current = true;
      setIsProcessing(true);

      const results: AutomationRuleExecutionResult[] = [];
      const cycleDetector = new AutomationCycleDetector();
      const fieldsWrittenThisPass = new Set<string>();

      try {
        // Filter rules matching this trigger type
        const matchingRules = enabledRules.filter((rule) => {
          if (rule.trigger.type !== event.type) return false;
          return matchesTriggerConfig(rule, event);
        });

        for (const rule of matchingRules) {
          const result: AutomationRuleExecutionResult = {
            ruleId: rule.id,
            ruleName: rule.name,
            triggered: true,
            conditionsPassed: false,
            actionsExecuted: false,
            timestamp: Date.now(),
          };

          // Cycle detection
          if (!cycleDetector.canExecute(rule.id)) {
            result.warning = 'Cycle detected — rule skipped';
            results.push(result);
            continue;
          }

          cycleDetector.startExecution(rule.id);

          try {
            // Evaluate conditions
            const { passed, warnings: condWarnings } = evaluateConditions(
              rule.conditions,
              event.record,
              fieldMapping,
            );

            if (condWarnings.length > 0) {
              result.warning = condWarnings.join('; ');
            }

            if (!passed) {
              result.conditionsPassed = false;
              results.push(result);
              continue;
            }

            result.conditionsPassed = true;

            // Execute actions
            const actionContext: ActionExecutorContext = {
              updateRecord,
              toast,
              currentUser,
              previousRecord: event.previousRecord,
              triggerRecord: event.record,
              fieldMapping,
              idField,
              writebackEnabled,
            };

            let allActionsSucceeded = true;
            for (const action of rule.actions) {
              // Per-field first-write-wins: skip if another rule already wrote this field
              const targetField = action.config.field ?? action.config.column;
              if (targetField && fieldsWrittenThisPass.has(targetField)) {
                continue; // First-match-wins per field
              }

              const actionResult = await executeAction(action, event.record, actionContext);

              if (actionResult.success && targetField) {
                fieldsWrittenThisPass.add(targetField);
                cycleDetector.recordFieldWrite(targetField, rule.id);
              }

              if (!actionResult.success) {
                allActionsSucceeded = false;
                if (actionResult.error) {
                  result.error = (result.error ? result.error + '; ' : '') + actionResult.error;
                }
                if (actionResult.warning) {
                  result.warning = (result.warning ? result.warning + '; ' : '') + actionResult.warning;
                }
                // Failed writeback does not block subsequent rules
              }
            }

            result.actionsExecuted = allActionsSucceeded;
          } finally {
            cycleDetector.endExecution(rule.id);
          }

          results.push(result);
        }

        // Collect cycle warnings
        const cycleWarnings = cycleDetector.getWarnings();
        if (cycleWarnings.length > 0) {
          setWarnings((prev) => [...prev, ...cycleWarnings]);
        }
      } finally {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }

      setExecutionLog((prev) => [...prev.slice(-50), ...results]); // Keep last 50 entries
      return results;
    },
    [enabled, enabledRules, fieldMapping, updateRecord, toast, currentUser, idField, writebackEnabled],
  );

  // onItemAdd detection via record-diff
  const detectNewRecords = useCallback(
    (currentRecords: Record<string, unknown>[]) => {
      if (!enabled || enabledRules.length === 0) return;

      const currentIds = new Set<string>();
      for (const record of currentRecords) {
        const id = String(record[idField] ?? record._id ?? '');
        if (id) currentIds.add(id);
      }

      // On first render, just seed the set
      if (previousRecordIdsRef.current.size === 0) {
        previousRecordIdsRef.current = currentIds;
        return;
      }

      // Find new IDs
      const newRecords = currentRecords.filter((record) => {
        const id = String(record[idField] ?? record._id ?? '');
        return id && !previousRecordIdsRef.current.has(id);
      });

      previousRecordIdsRef.current = currentIds;

      // Fire onItemAdd for each new record
      for (const record of newRecords) {
        handleDataEvent({ type: 'onItemAdd', record }).catch(console.error);
      }
    },
    [enabled, enabledRules, idField, handleDataEvent],
  );

  // Clear log when rules change
  useEffect(() => {
    setExecutionLog([]);
    setWarnings([]);
  }, [rules]);

  return {
    handleDataEvent,
    detectNewRecords,
    executionLog,
    isProcessing,
    warnings,
  };
}

/**
 * Checks if a rule's trigger config matches the event details.
 */
function matchesTriggerConfig(rule: AutomationRule, event: AutomationDataEvent): boolean {
  const config = rule.trigger.config;
  if (!config) return true; // No config = matches any event of this type

  switch (rule.trigger.type) {
    case 'onColumnMove': {
      if (config.toColumn && event.toColumn !== config.toColumn) return false;
      if (config.fromColumn && event.fromColumn !== config.fromColumn) return false;
      return true;
    }
    case 'onFieldChange': {
      if (config.field && event.field !== config.field) return false;
      return true;
    }
    case 'onThresholdCrossed': {
      if (config.field && event.field !== config.field) return false;
      return true;
    }
    default:
      return true;
  }
}
